import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversation } from '../context/ConversationContext';
import { useMsal } from "@azure/msal-react";
import { useAPI } from '../context/APIContext';
 
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  models: any[];
}
 
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isMobile, models }) => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  // const [logoutError, setLogoutError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const modeDropdownRef = useRef<HTMLDivElement>(null);
 
  const companyName = process.env.REACT_APP_COMPANY_NAME || 'RazorpayGPT';
  const companyLogo = '/logo.png';
 
  const {
    currentConversation,
    setCurrentConversation,
    startNewConversation,
    deleteConversation,
    selectedModel,
    setSelectedModel,
    activeConversationId,
    chatHistory,
    // setChatHistory, // Removed as it's not used
    fetchChatHistory,
    fetchAndSetConversation,
    setActiveConversationId,
    setCreateProject,
    selectedMode,
    setSelectedMode
  } = useConversation();
 
  const api = useAPI();
 
  const user = accounts[0];
  const userId = user?.idTokenClaims?.oid;
 
  useEffect(() => {
    if (userId) {
      fetchChatHistory(userId, 1, 30);
    }
  }, [userId, selectedMode]); // Matched original dependency array



  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setIsModelDropdownOpen(false);
      }
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target as Node)) {
        setIsModeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
 
  const fullName = user?.name || "";
  // const email = user?.username || "";
  const initials = fullName
    ? fullName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "";
 
  const handleDeleteConversation = async (e: React.MouseEvent, conversationId: string) => {
    e.preventDefault();
    e.stopPropagation();

    await deleteConversation(conversationId, userId);
  };
 
  const handleLogout = async () => {
    try {
      // setLogoutError(null);
      await instance.logoutPopup();
    } catch (error: any) {
      console.error("Logout failed:", error);
      // setLogoutError(error.message || "Failed to sign out. Please try again.");
    }
  };
 
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
 
  const handleShareConversation = async (e: React.MouseEvent, conversationId: string) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await api.shareConversation(conversationId, selectedMode);
      
      // Extract share ID from response (prefer share_id, then extract from share_url, then fallback to conversation ID)
      let shareId = '';
      
      if (res.share_id) {
        // Use share_id if available in response
        shareId = res.share_id;
      } else if (res.share_url && res.share_url.includes('/share/')) {
        // Extract share ID from share_url if available
        shareId = res.share_url.split('/share/')[1];
      } else {
        // Fallback to conversation ID
        shareId = conversationId;
      }
      
      // Construct share URL using current domain (works for any domain)
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      
      setShareUrl(shareUrl);
      setShowShareModal(true);
      setCopySuccess('');
    } catch (err) {
      console.error('Failed to share conversation', err);
    }
  };

  const handleModeChange = async (mode: 'internal' | 'external') => {
    setSelectedMode(mode);
    // Clear current conversation when switching modes without creating a new one
    setCurrentConversation(null);
    setActiveConversationId(null);
    // Clear all session-related storage
    sessionStorage.removeItem('conversation_id');
    sessionStorage.removeItem('session_id');
    sessionStorage.removeItem('thread_id');
    // Refresh chat history for the new mode using the proper function
    if (userId) {
      // Use the fetchChatHistory function from context which handles proper state management
      await fetchChatHistory(userId, 1, 30);
    }
  };

  // const handleModeChange = async (mode: 'internal' | 'external') => {
  //   console.log('Switching mode to:', mode);
  //   // setSelectedMode(mode);
  //   // Clear current conversation when switching modes without creating a new one
  //   setCurrentConversation(null);
  //   setActiveConversationId(null);
  //   // Clear all session-related storage
  //   console.log('Clearing session storage...');
  //   sessionStorage.removeItem('conversation_id');
  //   sessionStorage.removeItem('session_id');
  //   sessionStorage.removeItem('thread_id');
  //   // Refresh chat history for the new mode
  //   if (userId) {
  //     console.log('Fetching chat history for new mode:', mode);
  //     // Temporarily set the mode and fetch chat history
  //     const originalMode = selectedMode;
  //     // We need to manually call the API with the new mode since selectedMode hasn't updated yet
  //     const data = await api.getChatHistory(userId, 1, 30, mode);
  //     if (data && data.chats) {
  //       setChatHistory(data.chats);
  //     }
  //   }
  // };
 
  return (
    <>
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={() => setShowShareModal(false)}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-bold mb-4">Share Conversation</h2>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={shareUrl.startsWith('http://') || shareUrl.startsWith('https://') ? shareUrl : `${window.location.origin}${shareUrl}`}
                readOnly
                className="w-full border border-gray-300 rounded px-3 py-2 text-gray-700 bg-gray-100 focus:outline-none"
                onFocus={e => e.target.select()}
              />
              <button
                onClick={async () => {
                  const fullUrl = shareUrl.startsWith('http://') || shareUrl.startsWith('https://') ? shareUrl : `${window.location.origin}${shareUrl}`;
                  await navigator.clipboard.writeText(fullUrl);
                  setCopySuccess('Copied!');
                  setTimeout(() => setCopySuccess(''), 1500);
                }}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none"
              >
                Copy Link
              </button>
              {copySuccess && <span className="text-green-600 text-sm mt-1">{copySuccess}</span>}
            </div>
          </div>
        </div>
      )}
      <div
        className={`w-80 bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 flex-shrink-0 h-full
        fixed z-50 xl:relative xl:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        xl:translate-x-0`}
      >
        <div className="p-3 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
              <img src={companyLogo} alt="Company Logo" className="h-6 w-6 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">{companyName}</h1>
            </div>
            {isMobile && (
              <button onClick={onClose} className="inline-flex items-center justify-center gap-1 whitespace-nowrap text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0 hover:bg-accent h-7 rounded px-2 text-gray-500 hover:text-gray-700 flex-shrink-0 xl:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            )}
          </div>
          <button
            onClick={async () => { 
              setCreateProject(false); 
              await startNewConversation(); 
              navigate('/'); 
              // Auto-close sidebar on mobile when new chat is created
              if (isMobile) {
                onClose();
              }
            }}
            className="inline-flex items-center justify-center gap-1 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0 px-3 py-1.5 w-full bg-blue-500 hover:bg-blue-600 text-white h-8 rounded-md shadow-sm text-xs"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus w-3 h-3 mr-1" aria-hidden="true">
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
            New Chat
          </button>
        </div>

                <div className="p-3 border-b border-gray-200 bg-white flex-shrink-0">
          <label className="text-xs font-semibold text-gray-700 mb-2 block">AI Model</label>
          <div className="relative" ref={modelDropdownRef}>
            <button
              onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer hover:bg-white/90 transition-all duration-200 flex items-center justify-between"
            >
              <span className="truncate">{selectedModel || 'Select Model'}</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isModelDropdownOpen ? 'rotate-180' : ''}`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {isModelDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                {models.map((model) => (
                  <button
                    key={model.id}
                    onClick={() => {
                      setSelectedModel(model.id);
                      setIsModelDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-50 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg ${
                      selectedModel === model.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {model.id}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="p-3 border-b border-gray-200 bg-white flex-shrink-0">
          <label className="text-xs font-semibold text-gray-700 mb-2 block">Mode</label>
          <div className="relative" ref={modeDropdownRef}>
            <button
              onClick={() => setIsModeDropdownOpen(!isModeDropdownOpen)}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm cursor-pointer hover:bg-white/90 transition-all duration-200 flex items-center justify-between"
            >
              <span className="truncate capitalize">{selectedMode || 'Select Mode'}</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isModeDropdownOpen ? 'rotate-180' : ''}`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {isModeDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                <button
                  onClick={() => {
                    handleModeChange('internal');
                    setIsModeDropdownOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-50 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg ${
                    selectedMode === 'internal' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  Internal
                </button>
                <button
                  onClick={() => {
                    handleModeChange('external');
                    setIsModeDropdownOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-50 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg ${
                    selectedMode === 'external' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  External
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 min-h-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-700">Recent Chats</h3>
          </div>
          <div className="space-y-1">
            {chatHistory.map((chat) => {
              // Handle different chat object structures for internal vs external modes
              const chatId = selectedMode === 'internal' ? chat.id : chat._id;
              
              return (
                <div
                  key={chatId || chat.id || Math.random()}
                  onClick={() => {
                    // Guard against undefined chatId
                    if (!chatId) {
                      console.error('Chat ID is undefined, cannot load conversation');
                      return;
                    }
                    
                    if (selectedMode === 'internal' && userId) {
                      fetchAndSetConversation(chatId, userId);
                      navigate('/');
                    } else if (selectedMode === 'external' && userId) {
                      fetchAndSetConversation(chatId, userId);
                      setActiveConversationId(chatId);
                      navigate(`/c/${chatId}`);
                    }
                    
                    // Auto-close sidebar on mobile when chat is selected
                    if (isMobile) {
                      onClose();
                    }
                  }}
                className={`group p-1.5 rounded-md cursor-pointer transition-all ${
                  activeConversationId === chatId || currentConversation?.id === chatId
                    ? 'bg-blue-50 border border-blue-200 shadow-sm'
                    : 'hover:bg-white hover:shadow-sm border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <p className={`text-xs font-medium truncate ${
                        activeConversationId === chatId || currentConversation?.id === chatId
                          ? 'text-blue-700'
                          : 'text-gray-900'
                      }`}>
                        {(window as any).sanitizeTitle ? (window as any).sanitizeTitle(chat.title, 50, 'New Chat') : (chat.title || 'New Chat')}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {activeConversationId === chatId || currentConversation?.id === chatId ? 'Active' : formatDate(chat.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleShareConversation(e, chatId)}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Share"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => handleDeleteConversation(e, chatId)}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                      title="Delete"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        <div className="p-3 sm:p-4 border-t border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg sm:rounded-xl bg-gray-50">
            <span className="relative flex shrink-0 overflow-hidden rounded-full w-7 h-7 sm:w-9 sm:h-9 flex-shrink-0">
              <span className="flex h-full w-full items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold text-sm">
                {initials || 'U'}
              </span>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{fullName || 'User'}</p>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap text-xs font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent rounded-md text-gray-400 hover:text-gray-600 flex-shrink-0 px-2 py-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16,17 21,12 16,7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;